import { Box, Flashbar, Link } from '@amzn/awsui-components-react';
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { tabbable } from 'tabbable';
import { useFlashbars } from '@/src/store/flashbar.context';
import { useLSE } from '@/src/store/lse.context';
import { useUser } from '@/src/store/user.context';
import { FlashbarItem } from '@/src/types/common';
import { i18nKeys, withI18NPrefix } from '@/src/utils/i18n.utils';
import { bannerFlashbar, errorFlashbar, infoFlashbar, successFlashbar } from '@/src/utils/notification.utils';
import { useAuth } from '@/src/store/auth.context';
import { config } from '@/src/config/app-config';
import { hashCode } from '@/src/utils/string.utils';

const FLASHBAR_FOCUS_INTERVAL = 20;
const FLASHBAR_FOCUS_TIMEOUT = 1000;

const flashbarPerLevel = (level: string) => {
  switch (level) {
    case 'success':
      return successFlashbar;
    case 'info':
      return infoFlashbar;
    case 'error':
      return errorFlashbar;
    default:
      return bannerFlashbar;
  }
};

const NotificationLink = ({ url }: { url: string }) => {
  const { t } = useTranslation();
  return (
    <Link href={url} external color="inverted">
      {t(i18nKeys.general.learnMore)}
    </Link>
  );
};

const NotificationBody: React.FC<{ url?: string | null; content?: string | null }> = ({ url, content }) => {
  return (
    <Box>
      {content}
      {!!url && !!content && <br />}
      {!!url && <NotificationLink url={url} />}
    </Box>
  );
};

const TopLevelFlashbars: React.FC = memo(() => {
  const { user } = useUser();
  const { flashbars } = useFlashbars();
  const { authClient } = useAuth();
  const { t } = useTranslation();
  const flashbarRef = useRef<HTMLDivElement>();
  const flashbarFocusInterval = useRef<number>();

  const { currentLSE, loadCurrentLSE, globalNotifications, loadGlobalNotifications } = useLSE();
  const [dismissedNotificationsMap, setDismissedNotificationsMap] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    authClient.isSignedIn().then((signedIn) => {
      if (signedIn) {
        config.ff.enableGlobalNotifications ? loadGlobalNotifications() : loadCurrentLSE();
      }
    });
  }, [authClient]);

  const focusFlashbar = () => {
    if (!flashbarRef.current) {
      return;
    }

    const elements = tabbable(flashbarRef.current);

    if (!elements.length) {
      return;
    }

    clearInterval(flashbarFocusInterval.current);
    const normalizedFocusableText = ((elem) => {
      if (!elem) return null;
      const textContent = elem.textContent?.normalize()?.toLocaleLowerCase()?.replace(/\s+/g, '');
      const href = elem instanceof HTMLAnchorElement ? elem.href : '';
      return textContent + (href && `::${href}`);
    })(elements?.[0]);
    if (normalizedFocusableText && sessionStorage.getItem(`flashbar-focus-${normalizedFocusableText}`) !== 'true') {
      elements[0].focus();
      sessionStorage.setItem(`flashbar-focus-${normalizedFocusableText}`, 'true');
    }
  };

  const i18nStrings = {
    ariaLabel: t(i18nKeys.general.info),
    errorIconAriaLabel: t(i18nKeys.general.error),
    inProgressIconAriaLabel: t(i18nKeys.general.inProgress),
    infoIconAriaLabel: t(i18nKeys.general.info),
    notificationBarAriaLabel: t(i18nKeys.general.notifications),
    notificationBarText: t(i18nKeys.general.notifications),
    successIconAriaLabel: t(i18nKeys.general.success),
    warningIconAriaLabel: t(i18nKeys.general.warning),
  };

  /**
   * Iterate through all flashbar items and handle translation of any i18n keys that might be present.
   */
  const localizedFlashbarItems = useMemo((): FlashbarItem[] => {
    const notifications = [currentLSE, ...(globalNotifications || [])].filter((item) => !!item);
    const notificationFlashbars = notifications?.reduce((accum, not) => {
      const pseudoId =
        not?.id ??
        hashCode(not?.text || '')
          .toString(36)
          .substring(2);
      const hasBeenDismissed = pseudoId && pseudoId in dismissedNotificationsMap && dismissedNotificationsMap[pseudoId];
      if (not?.text && !hasBeenDismissed) {
        accum.push({
          ...flashbarPerLevel(not?.level ?? '')(
            not?.header ?? not?.text,
            <NotificationBody content={not?.header ? not?.text : null} url={not?.url} />,
            undefined,
            () => {
              setDismissedNotificationsMap((prev) => ({ ...prev, [pseudoId]: true }));
            }
          ),
          dismissLabel: t(i18nKeys.general.dismissMessage),
        });
      }
      return accum;
    }, [] as FlashbarItem[]);
    return (flashbars || [])
      .map((item) => {
        if (item.i18nKeys && item.i18nKeys.length > 0) {
          item = Object.assign({}, item);
          item.i18nKeys.forEach((key) => {
            const value = t(key);
            if (value) {
              if (typeof item.header === 'string') {
                item.header = item.header.replace(withI18NPrefix(key), value);
              }
              if (typeof item.content === 'string') {
                item.content = item.content.replace(withI18NPrefix(key), value);
              }
            }
          });
        }
        item.dismissLabel = t(i18nKeys.general.dismissMessage);
        return item;
      })
      .slice()
      .reverse()
      .concat(notificationFlashbars);
  }, [flashbars, globalNotifications, dismissedNotificationsMap, currentLSE, t]);

  useEffect(() => {
    if (!localizedFlashbarItems.length) {
      return;
    }

    const intervalId = setInterval(() => focusFlashbar(), FLASHBAR_FOCUS_INTERVAL);

    // The flashbars are not updated immediately so we need to check repeatedly to focus when one is added
    flashbarFocusInterval.current = intervalId;
    setTimeout(() => clearInterval(intervalId), FLASHBAR_FOCUS_TIMEOUT);
  }, [localizedFlashbarItems?.length]);

  if (!user) {
    return <></>;
  }
  return (
    <div data-testid="flashbar-wrapper" ref={flashbarRef as any}>
      <Flashbar stackItems i18nStrings={i18nStrings} items={localizedFlashbarItems} />
    </div>
  );
});

export default TopLevelFlashbars;
